import { Form, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { User } from '@/hooks/useUser';
import { config } from '@/config';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface ProfileFormInputs {
  firstName: string;
  lastName: string;
}

/**
 * Who you are: name, the address you sign in with, and which organizations you
 * belong to. Behavior is the former Profile page's, unchanged — only the password,
 * email and notification sections moved to siblings.
 */
export default function ProfileSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    register,
    control,
    formState: { errors, isDirty },
    reset,
  } = useForm<ProfileFormInputs>({
    // `values` (not `defaultValues`) keeps the form synced to the user data once it
    // loads, so the fields always reflect the current name. keepDirtyValues preserves
    // anything being typed if the user refetches mid-edit.
    values: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
    },
    resetOptions: { keepDirtyValues: true },
  });

  const updateProfileMutation = useMutation<User, Error, ProfileFormInputs>({
    mutationFn: async (data) => {
      const response = await axios.patch<User>(`${config.backendApi}/api/users/me`, data);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });

  if (!user) return null;

  return (
    <PageContainer width="medium">
      <PageHeader title="Profile" subtitle="Your name and how you sign in" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2 space-y-4 md:space-y-5">
          <section className="bg-card border border-border rounded-lg">
            <header className="px-5 py-4 border-b border-border">
              <h2 className="text-sm font-semibold">Personal information</h2>
            </header>

            <Form
              control={control}
              onSubmit={({ data }) => { updateProfileMutation.mutate(data); }}
              className="p-5 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="profile-firstName">First name</Label>
                  <Input
                    id="profile-firstName"
                    type="text"
                    aria-invalid={!!errors.firstName}
                    {...register('firstName', {
                      required: 'First name is required',
                      minLength: { value: 1, message: 'First name cannot be empty' },
                    })}
                  />
                  {errors.firstName ? (
                    <p className="text-xs text-destructive">{errors.firstName.message}</p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="profile-lastName">
                    Last name <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="profile-lastName"
                    type="text"
                    aria-invalid={!!errors.lastName}
                    {...register('lastName')}
                  />
                  {errors.lastName ? (
                    <p className="text-xs text-destructive">{errors.lastName.message}</p>
                  ) : null}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="profile-email">Email</Label>
                <Input id="profile-email" type="email" value={user.email} readOnly disabled />
                {/* Points at the sibling page rather than repeating the explanation:
                    the primary address *can* change, just not from this field. */}
                <p className="text-xs text-muted-foreground">
                  Your sign-in address. Add or change it under Email addresses.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Organizations</Label>
                {user.organizations.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {user.organizations.map((org) => (
                      <Badge key={org.id} variant="secondary">
                        {org.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No organizations</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Organizations are managed via team membership.
                </p>
              </div>

              {updateProfileMutation.isError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Error updating profile:</strong> {updateProfileMutation.error.message}
                  </AlertDescription>
                </Alert>
              ) : null}

              {updateProfileMutation.isSuccess ? (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>Profile updated successfully.</AlertDescription>
                </Alert>
              ) : null}

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                {isDirty ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { reset(); }}
                    disabled={updateProfileMutation.isPending}
                  >
                    Reset
                  </Button>
                ) : null}
                <Button type="submit" disabled={!isDirty || updateProfileMutation.isPending}>
                  {updateProfileMutation.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </Form>
          </section>
        </div>

        {/* Right column — account status sidecar. */}
        <aside className="space-y-4 md:space-y-5">
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Account status</h2>
            <dl className="text-xs space-y-2">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">User ID</dt>
                <dd className="font-mono text-right truncate">{user.id}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Email</dt>
                <dd className="truncate text-right">{user.email}</dd>
              </div>
            </dl>

            {user.isAdmin ? (
              <>
                <Separator className="my-4" />
                <div
                  className={cn('rounded-md border p-3 flex items-start gap-2.5', 'border-sev-3 bg-sev-3-soft')}
                  role="status"
                >
                  <ShieldAlert className="h-4 w-4 text-sev-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">SaaS administrator</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      You have platform-wide admin access.
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </section>
        </aside>
      </div>
    </PageContainer>
  );
}
